import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  fetchAnalystConversation,
  fetchAnalystConversationTurns,
  fetchCaseAssumptions,
  fetchCaseTopology,
  streamAnalystResponse,
} from '@/api/analyst';
import { exportConversation as exportConversationShared } from '@/utils/agentExport';
import { getProject } from '@/api/projects';

// Result action types that generate workspace history items.
const RESULT_ACTION_TYPES = new Set([
  'get_lca_results',
  'get_tea_results',
  'get_comparison_results',
  'get_sensitivity_results',
  'run_sensitivity_sweep',
  'run_sensitivity_analysis',
  'create_workspace_content',
]);

// Action types that update the assumptions map rather than workspace history.
const ASSUMPTIONS_ACTION_TYPES = new Set(['get_study_assumptions']);

// Action types that carry a case_id we can map to a pathway name.
const PATHWAY_CREATION_ACTIONS = new Set(['create_pathway_from_template', 'copy_case', 'build_pathway']);

// Internal housekeeping tools — hidden from the progress checklist.
// They still run and are counted in cumulative progress, but cluttering
// the step list with "Read parameters" × 6 adds noise without value.
export const SCAFFOLDING_TOOLS = new Set([
  'find_param',
  'get_node_params',
  'get_pathway_summary',
  'list_pathways',
  'get_balance_results',
  'get_study_assumptions',
  // System (BTM asset / future hydrogen / power dispatch) housekeeping —
  // hide reads that don't change state from the progress checklist.
  'load_skill',
  'list_systems',
  'list_system_templates',
  'get_system_params',
  'get_generator_params',
  'get_system_status',
]);

function generateLabel(actionType, pathwayName, history, data) {
  const countOfType = history.filter(h => h.actionType === actionType).length + 1;
  const suffix = pathwayName ? ` — ${pathwayName}` : ` #${countOfType}`;
  switch (actionType) {
    case 'get_lca_results':
      return `LCA${suffix}`;
    case 'get_tea_results':
      return `TEA${suffix}`;
    case 'get_sensitivity_results':
      return `Sensitivity${suffix}`;
    case 'run_sensitivity_analysis':
      return 'Sensitivity Analysis';
    case 'get_comparison_results':
      return countOfType > 1 ? `Comparison #${countOfType}` : 'Comparison';
    case 'run_sensitivity_sweep': {
      const paramLabel = (data?.param_name ?? 'param').replace(/_/g, ' ');
      return `Sweep — ${paramLabel}${suffix}`;
    }
    case 'create_workspace_content':
      return data?.title ?? 'Custom';
    // System (BTM asset / future hydrogen / power dispatch) actions
    case 'load_skill':
      return data?.skill ? `Skill: ${data.skill}` : 'Skill';
    case 'create_system_from_template':
      return data?.name ? `System: ${data.name}` : 'Created system';
    case 'create_systems_batch': {
      const n = Array.isArray(data?.systems) ? data.systems.length : 0;
      return n ? `Created ${n} systems` : 'Created systems';
    }
    case 'run_system':
      return data?.system_id ? `Optimization${suffix}` : 'Optimization';
    case 'get_system_results':
      return countOfType > 1 ? `System Results #${countOfType}` : 'System Results';
    case 'compare_systems':
      return 'System Comparison';
    case 'set_solar_generator':
      return 'Solar configured';
    case 'set_wind_generator':
      return 'Wind configured';
    case 'set_storage_generator':
      return 'Storage configured';
    case 'set_thermal_generator':
      return 'Thermal configured';
    case 'set_pathway_powerplant':
      return 'Pathway powerplant linked';
    case 'delete_generator':
      return 'Generator removed';
    case 'set_system_params':
      return 'System params updated';
    default:
      return actionType.replace(/_/g, ' ');
  }
}

/**
 * Resolve the pathway name for a result action.
 * Uses case_id → name map (authoritative), falls back to data.pathway_name.
 */
function resolvePathwayName(s, data) {
  if (data?.case_id && s.workspaceCaseIdToName[data.case_id]) {
    return s.workspaceCaseIdToName[data.case_id];
  }
  return data?.pathway_name ?? null;
}

// True when the workspace has anything worth showing — analysis results,
// a pending review, OR a constructed pathway (topology / assumptions / name).
// Used by reset sites so a build-only turn doesn't hide the panel at onDone.
function hasWorkspaceContent(s) {
  return (
    s.workspaceHistory.length > 0 ||
    !!s.workspaceReview ||
    Object.keys(s.workspaceCaseIdToName).length > 0 ||
    Object.keys(s.workspaceTopologies).length > 0 ||
    Object.keys(s.workspaceAssumptions).length > 0
  );
}

const TYPE_LABEL = {
  get_lca_results: 'LCA',
  get_tea_results: 'TEA',
  get_sensitivity_results: 'Sensitivity',
  run_sensitivity_analysis: 'Sensitivity',
  get_comparison_results: 'Comparison',
  run_sensitivity_sweep: 'Sweep',
  create_workspace_content: 'Custom',
};

export const typeIcon = actionType => TYPE_LABEL[actionType] ?? '';

const useAnalystStore = create()(
  immer(
    persist(
      () => ({
        // ── Chat state ──────────────────────────────────────────────────────
        messages: [],
        conversationId: null,
        projectId: null,
        projectName: null, // resolved lazily when projectId is known (export display)
        studyMode: false,
        isStreaming: false,
        isLoadingConversation: false,
        abortController: null, // transient — not persisted
        _nextTextIsNewBubble: false,
        pendingActions: [],

        // ── Workspace state ─────────────────────────────────────────────────
        workspaceStatus: 'empty', // 'empty' | 'running' | 'results'

        // Cumulative step counts for the progress bar — only ever go up.
        // Not persisted (transient per-run state, rebuilt from events).
        cumulativeStepsDone: 0,
        cumulativeStepsTotal: 0,

        // History: all result items ever produced in this conversation.
        workspaceHistory: [], // [{id, actionType, label, icon, data, timestamp}]
        // IDs of history items currently open as tabs.
        workspaceTabs: [], // string[]
        // The ID of the active tab, or null to show the Index.
        workspaceActiveTab: null,

        // Maps case_id → pathway name, built as pathways are created.
        // Used by resolvePathwayName() to label results correctly.
        workspaceCaseIdToName: {},

        // Maps case_id → pathway_id, built as pathways are created.
        // Used by NodeParamEditor to open the correct node param form.
        workspaceCaseIdToPathwayId: {},

        // Maps case_id → get_study_assumptions result data.
        // Updated every time the agent calls get_study_assumptions.
        // Drives the persistent Assumptions tab in the workspace.
        workspaceAssumptions: {},

        // Maps case_id → topology data {nodes, edges, flows}.
        // Fetched alongside assumptions; drives the PathwayDiagram in the Assumptions view.
        workspaceTopologies: {},

        // True when params were edited via the node editor after the last analysis
        // completed. The workspace shows a stale-results banner until a new analysis runs.
        workspaceResultsStale: false,

        // Debug stats from the last turn (only populated when AI_DEBUG_UI=true).
        debugStats: null,
        // Cumulative debug stats across all turns in this session.
        debugStatsHistory: [],

        // Set by propose_analysis — holds {case_ids, comparison, summary}.
        // Drives the review state in the workspace before analysis runs.
        workspaceReview: null,

        // Transient — accumulates the current streaming text response for live
        // display. Cleared on any tool call (intermediate text discarded) or on
        // onDone (text is now committed to the messages array). Never persisted.
        streamingText: '',
      }),
      {
        name: 'sesame-analyst',
        // Conversations are persisted by the server. Keep only this UI preference locally.
        version: 15,
        migrate: state => ({ studyMode: state?.studyMode ?? false }),
        partialize: state => ({ studyMode: state.studyMode }),
      },
    ),
  ),
);

const { setState: set, getState: get } = useAnalystStore;

export const analystActions = {
  exportConversation: () => {
    const { messages, projectId, projectName } = get();
    const projectUrl = projectId ? `${window.location.origin}/projects/${projectId}/dashboard` : null;
    exportConversationShared({
      messages,
      projectId,
      projectLabel: projectName,
      projectUrl,
    });
  },

  // Lazy-fetch the project's display name once we know its id. Tolerant of
  // network failure — the export still renders with the URL-only fallback.
  resolveProjectName: async projectId => {
    if (!projectId) return;
    try {
      const project = await getProject(projectId);
      if (project?.name) {
        set(s => {
          if (s.projectId === projectId) s.projectName = project.name;
        });
      }
    } catch {
      /* swallow — projectName stays null and the export uses the URL-only fallback */
    }
  },

  clearConversation: () => {
    set(s => {
      s.messages = [];
      s.isStreaming = false;
      s.isLoadingConversation = false;
      s.conversationId = null;
      s.projectId = null;
      s.projectName = null;
      s.abortController = null;
      s.pendingActions = [];
      s.workspaceStatus = 'empty';
      s.workspaceHistory = [];
      s.workspaceTabs = [];
      s.workspaceActiveTab = null;
      s.workspaceCaseIdToName = {};
      s.workspaceCaseIdToPathwayId = {};
      s.workspaceAssumptions = {};
      s.workspaceTopologies = {};
      s.workspaceReview = null;
      s.workspaceResultsStale = false;
      s.cumulativeStepsDone = 0;
      s.cumulativeStepsTotal = 0;
      s.debugStats = null;
      s.debugStatsHistory = [];
    });
  },

  loadConversation: async conversationId => {
    set(s => {
      s.isLoadingConversation = true;
    });
    try {
      const [conversation, turns] = await Promise.all([
        fetchAnalystConversation(conversationId),
        fetchAnalystConversationTurns(conversationId),
      ]);

      set(s => {
        s.messages = [];
        s.conversationId = conversation.id;
        s.projectId = conversation.project?.id ?? null;
        s.projectName = conversation.project?.name ?? null;
        s.isStreaming = false;
        s.abortController = null;
        s.pendingActions = [];
        s.workspaceStatus = 'empty';
        s.workspaceHistory = [];
        s.workspaceTabs = [];
        s.workspaceActiveTab = null;
        s.workspaceCaseIdToName = {};
        s.workspaceCaseIdToPathwayId = {};
        s.workspaceAssumptions = {};
        s.workspaceTopologies = {};
        s.workspaceReview = null;
        s.workspaceResultsStale = false;
        s.cumulativeStepsDone = 0;
        s.cumulativeStepsTotal = 0;
        s.debugStats = null;
        s.debugStatsHistory = [];
      });

      for (const turn of turns) {
        const metadata = turn.metadata ?? {};
        if (turn.role === 'action') {
          if (metadata.data) analystActions.handleAction(metadata.action_type, metadata.data);
          continue;
        }
        set(s => {
          if (turn.role === 'system' && metadata.kind === 'project_replaced') {
            s.workspaceStatus = 'empty';
            s.workspaceHistory = [];
            s.workspaceTabs = [];
            s.workspaceActiveTab = null;
            s.workspaceCaseIdToName = {};
            s.workspaceCaseIdToPathwayId = {};
            s.workspaceAssumptions = {};
            s.workspaceTopologies = {};
            s.workspaceReview = null;
            s.workspaceResultsStale = false;
          }
          s.messages.push({
            role: turn.role,
            content: turn.content ?? '',
            intermediate: metadata.intermediate ?? false,
            hiddenFromUI: metadata.hidden_from_ui ?? false,
            kind: metadata.kind,
            createdAt: turn.created_at,
          });
        });
      }

      const missingAssumptionCaseIds = Object.keys(get().workspaceCaseIdToName).filter(
        id => !get().workspaceAssumptions[id],
      );
      set(s => {
        s.pendingActions = [];
        s.cumulativeStepsDone = 0;
        s.cumulativeStepsTotal = 0;
        s.workspaceStatus = hasWorkspaceContent(s) ? 'results' : 'empty';
        s.isLoadingConversation = false;
      });
      for (const caseId of missingAssumptionCaseIds) {
        fetchCaseAssumptions(caseId)
          .then(data =>
            set(s => {
              s.workspaceAssumptions[caseId] = data;
            }),
          )
          .catch(() => {});
      }
      return true;
    } catch {
      set(s => {
        s.isLoadingConversation = false;
      });
      return false;
    }
  },

  toggleStudyMode: () =>
    set(s => {
      s.studyMode = !s.studyMode;
    }),

  stopStreaming: () => {
    const { abortController } = get();
    abortController?.abort();
    set(s => {
      s.isStreaming = false;
      s.abortController = null;
      s.streamingText = '';
      s.pendingActions = [];
      s.messages.push({ role: 'assistant', content: 'Stopped.' });
      if (s.workspaceStatus === 'running') {
        s.workspaceStatus = hasWorkspaceContent(s) ? 'results' : 'empty';
      }
    });
  },

  // Close a result tab. Falls back to Index (null) if it was active.
  closeTab: id =>
    set(s => {
      s.workspaceTabs = s.workspaceTabs.filter(t => t !== id);
      if (s.workspaceActiveTab === id) {
        const remaining = s.workspaceTabs;
        s.workspaceActiveTab = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      }
    }),

  // Open a history item as a tab (reopen from index).
  openTab: id =>
    set(s => {
      if (!s.workspaceTabs.includes(id)) {
        s.workspaceTabs.push(id);
      }
      s.workspaceActiveTab = id;
    }),

  setActiveTab: id =>
    set(s => {
      s.workspaceActiveTab = id;
    }),

  refreshAssumptions: async caseId => {
    try {
      const [data, topo] = await Promise.all([
        fetchCaseAssumptions(caseId),
        fetchCaseTopology(caseId).catch(() => null),
      ]);
      set(s => {
        s.workspaceAssumptions[caseId] = data;
        if (topo) s.workspaceTopologies[caseId] = topo;
        if (s.workspaceHistory.length > 0) {
          s.workspaceResultsStale = true;
        }
      });
    } catch {
      // Silently ignore — stale data is acceptable after a direct edit.
    }
  },

  handleAction: (actionType, data) => {
    if (actionType === 'propose_analysis') {
      set(s => {
        s._nextTextIsNewBubble = true;
        const idx = s.pendingActions.findIndex(a => a.actionType === 'propose_analysis');
        if (idx !== -1) s.pendingActions.splice(idx, 1);
        s.workspaceReview = data;
        if (s.workspaceStatus === 'empty' || s.workspaceStatus === 'running') {
          s.workspaceStatus = 'results';
        }
        s.workspaceActiveTab = '__assumptions__';
        // Extract embedded assumptions (propose_analysis now reads them internally).
        if (data.assumptions) {
          for (const [caseId, assumptionsData] of Object.entries(data.assumptions)) {
            s.workspaceAssumptions[caseId] = assumptionsData;
          }
        }
      });
      // Fetch topologies for all case_ids.
      (data.case_ids ?? []).forEach(caseId => {
        fetchCaseTopology(caseId)
          .then(topo =>
            set(s => {
              s.workspaceTopologies[caseId] = topo;
            }),
          )
          .catch(() => {});
      });
      return;
    }

    // When a pathway is created, map its case_id → name and pathway_id,
    // and eagerly fetch topology so the Assumptions tab populates before propose_analysis.
    if (PATHWAY_CREATION_ACTIONS.has(actionType) && data?.case_id) {
      const name = data?.name || data?.pathway_name;
      const pathwayId = data?.pathway_id;
      set(s => {
        if (name) s.workspaceCaseIdToName[data.case_id] = name;
        if (pathwayId) s.workspaceCaseIdToPathwayId[data.case_id] = pathwayId;
      });
      fetchCaseTopology(data.case_id)
        .then(topo =>
          set(s => {
            s.workspaceTopologies[data.case_id] = topo;
            // Auto-navigate to the Assumptions tab on the first topology if no
            // results exist yet and the user hasn't clicked away from the Index tab.
            if (
              Object.keys(s.workspaceTopologies).length === 1 &&
              s.workspaceHistory.length === 0 &&
              (s.workspaceActiveTab === null || s.workspaceActiveTab === '__assumptions__')
            ) {
              s.workspaceActiveTab = '__assumptions__';
            }
          }),
        )
        .catch(() => {});
    }

    // Assumptions results update the workspaceAssumptions map — they don't
    // add history tabs but do power the persistent Assumptions tab.
    if (ASSUMPTIONS_ACTION_TYPES.has(actionType) && !data?.error && data?.case_id) {
      const caseIdForTopo = data.case_id;
      set(s => {
        s.workspaceAssumptions[caseIdForTopo] = data;
        const runningIdx = s.pendingActions.findIndex(a => a.actionType === actionType && a.status === 'running');
        if (runningIdx !== -1) {
          s.pendingActions[runningIdx] = { actionType, data, status: 'done' };
        }
      });
      // Fetch topology in parallel — no need to block the stream.
      fetchCaseTopology(caseIdForTopo)
        .then(topo =>
          set(s => {
            s.workspaceTopologies[caseIdForTopo] = topo;
          }),
        )
        .catch(() => {});
      return;
    }

    // Joint LCA + TEA tool. Synthesize two atomic result entries
    // (one LCA, one TEA) so existing renderers, dedup, tabs, and
    // history all work without further special-casing. The joint
    // tool stays a backend-only token-efficiency optimization; the
    // UI treats results as if the agent had called the two atomic
    // tools separately.
    if (actionType === 'get_analysis_results') {
      set(s => {
        s._nextTextIsNewBubble = true;
        s.cumulativeStepsDone += 1;

        const runningIdx = s.pendingActions.findIndex(a => a.actionType === actionType && a.status === 'running');
        if (runningIdx !== -1) {
          s.pendingActions[runningIdx] = { actionType, data, status: 'done' };
        }

        if (data?.error) return;

        const caseId = data.case_id;
        const pathwayId = data.pathway_id;
        const isComparisonStudy = s.workspaceReview?.comparison === true;

        const synthesizeAtomic = (synthActionType, payload) => {
          if (!payload) return;
          const synthData = { ...payload, case_id: caseId, pathway_id: pathwayId };
          const name = resolvePathwayName(s, synthData);

          // Inline result line in chat — suppressed in comparison
          // studies, matching the rule the atomic LCA/TEA branch uses.
          if (!isComparisonStudy) {
            s.messages.push({
              role: 'result',
              actionType: synthActionType,
              data: synthData,
              pathwayName: name,
            });
          }

          const existingIdx = caseId
            ? s.workspaceHistory.findIndex(h => h.actionType === synthActionType && h.data?.case_id === caseId)
            : -1;

          if (existingIdx !== -1) {
            const resolvedLabel = generateLabel(
              synthActionType,
              name,
              s.workspaceHistory.filter((_, i) => i !== existingIdx),
              synthData,
            );
            s.workspaceHistory[existingIdx] = {
              ...s.workspaceHistory[existingIdx],
              label: resolvedLabel,
              data: synthData,
            };
            const existingId = s.workspaceHistory[existingIdx].id;
            if (!isComparisonStudy) {
              if (!s.workspaceTabs.includes(existingId)) s.workspaceTabs.push(existingId);
              s.workspaceActiveTab = existingId;
            }
            return;
          }

          const id = `${Date.now()}-${s.workspaceHistory.length}`;
          s.workspaceHistory.push({
            id,
            actionType: synthActionType,
            label: generateLabel(synthActionType, name, s.workspaceHistory, synthData),
            icon: typeIcon(synthActionType),
            data: synthData,
            timestamp: Date.now(),
          });
          if (!isComparisonStudy) {
            s.workspaceTabs.push(id);
            s.workspaceActiveTab = id;
          }
        };

        synthesizeAtomic('get_lca_results', data.lca);
        synthesizeAtomic('get_tea_results', data.tea);
      });
      return;
    }

    if (RESULT_ACTION_TYPES.has(actionType)) {
      set(s => {
        s._nextTextIsNewBubble = true;
        s.cumulativeStepsDone += 1;
        // Inline result line in chat.
        // Skip individual LCA/TEA when this is a comparison study —
        // the comparison table shows everything more clearly.
        if (!data?.error) {
          const isIndividual = actionType === 'get_lca_results' || actionType === 'get_tea_results';
          const isComparisonStudy = s.workspaceReview?.comparison === true;
          if (!(isIndividual && isComparisonStudy)) {
            s.messages.push({
              role: 'result',
              actionType,
              data,
              pathwayName: resolvePathwayName(s, data),
            });
          }
        }
        // Mark done in pendingActions.
        const runningIdx = s.pendingActions.findIndex(a => a.actionType === actionType && a.status === 'running');
        if (runningIdx !== -1) {
          s.pendingActions[runningIdx] = { actionType, data, status: 'done' };
        }
        if (!data?.error) {
          // Deduplicate: if there's already a history entry for this
          // actionType + case_id, update it in place rather than adding
          // a new entry (prevents "LCA #1" + "LCA — Houston" duplicates
          // when the name resolves after the first result was recorded).
          const caseId = data?.case_id;
          const existingIdx = caseId
            ? s.workspaceHistory.findIndex(h => h.actionType === actionType && h.data?.case_id === caseId)
            : -1;

          if (existingIdx !== -1) {
            const name = resolvePathwayName(s, data);
            const resolvedLabel = generateLabel(
              actionType,
              name,
              s.workspaceHistory.filter((_, i) => i !== existingIdx),
              data,
            );
            s.workspaceHistory[existingIdx] = {
              ...s.workspaceHistory[existingIdx],
              label: resolvedLabel,
              data,
            };
            const existingId = s.workspaceHistory[existingIdx].id;
            const isIndividual = actionType === 'get_lca_results' || actionType === 'get_tea_results';
            const isComparisonStudy = s.workspaceReview?.comparison === true;
            if (!(isIndividual && isComparisonStudy)) {
              if (!s.workspaceTabs.includes(existingId)) s.workspaceTabs.push(existingId);
              s.workspaceActiveTab = existingId;
            }
          } else {
            const name = resolvePathwayName(s, data);
            const label = generateLabel(actionType, name, s.workspaceHistory, data);
            const id = `${Date.now()}-${s.workspaceHistory.length}`;

            // When a second comparison arrives, retroactively number the first
            // one so both have distinguishable labels (#1, #2, ...).
            if (actionType === 'get_comparison_results') {
              const priorComparisons = s.workspaceHistory.filter(h => h.actionType === 'get_comparison_results');
              if (priorComparisons.length === 1 && priorComparisons[0].label === 'Comparison') {
                priorComparisons[0].label = 'Comparison #1';
              }
            }

            // run_sensitivity_analysis is a composite — only the synthesized
            // per-case entries go into history, not the parent wrapper.
            const isSensComposite = actionType === 'run_sensitivity_analysis';
            if (!isSensComposite) {
              s.workspaceHistory.push({
                id,
                actionType,
                label,
                icon: typeIcon(actionType),
                data,
                timestamp: Date.now(),
              });
            }
            // For comparison studies, only auto-open comparison/sweep tabs.
            // Individual LCA/TEA go to history (accessible from index) but not tabs.
            const isIndividual = actionType === 'get_lca_results' || actionType === 'get_tea_results';
            const isComparisonStudy = s.workspaceReview?.comparison === true;
            if (!isSensComposite && !(isIndividual && isComparisonStudy)) {
              s.workspaceTabs.push(id);
              s.workspaceActiveTab = id;
            }
          }
          // Do NOT flip workspaceStatus here — stay in 'running' until
          // onDone fires. This prevents the progress/results view from
          // jumping back and forth during a multi-pathway run.

          // For run_sensitivity_analysis, synthesize individual sensitivity
          // entries per case so existing SensitivityResultCard renders.
          if (actionType === 'run_sensitivity_analysis') {
            const now = Date.now();
            (data.cases ?? []).forEach((c, ci) => {
              if (c.error || !c.sensitivity) return;
              const pathwayLabel = c.pathway_name || c.case_id;
              const synthId = `${now}-synth-sens-${ci}`;
              s.workspaceHistory.push({
                id: synthId,
                actionType: 'get_sensitivity_results',
                label: `Sensitivity — ${pathwayLabel}`,
                icon: typeIcon('get_sensitivity_results'),
                data: { ...c.sensitivity, case_id: c.case_id, pathway_name: c.pathway_name },
                timestamp: now,
                synthetic: true,
              });
              s.workspaceTabs.push(synthId);
              s.workspaceActiveTab = synthId;
            });
          }

          // For comparison results, synthesize individual LCA/TEA history
          // items for any pathway that wasn't individually analyzed (e.g.
          // when the agent goes straight to comparison without calling
          // get_lca_results / get_tea_results per pathway first).
          if (actionType === 'get_comparison_results') {
            const now = Date.now();
            (data.cases ?? []).forEach((c, ci) => {
              const pathwayLabel = c.name || c.pathway_name;
              const cCaseId = c.case_id;

              // Match by case_id first (most reliable), fall back to label.
              // Synthetic items carry case_id so later real get_lca/tea_results
              // calls can deduplicate against them and replace the empty breakdown.
              if (c.lca_total != null) {
                const alreadyHasLca = cCaseId
                  ? s.workspaceHistory.some(h => h.actionType === 'get_lca_results' && h.data?.case_id === cCaseId)
                  : s.workspaceHistory.some(
                      h => h.actionType === 'get_lca_results' && h.label === `LCA — ${pathwayLabel}`,
                    );
                if (!alreadyHasLca) {
                  s.workspaceHistory.push({
                    id: `${now}-synth-lca-${ci}`,
                    actionType: 'get_lca_results',
                    label: `LCA — ${pathwayLabel}`,
                    icon: typeIcon('get_lca_results'),
                    data: {
                      case_id: cCaseId,
                      pathway_id: c.pathway_id,
                      total: c.lca_total,
                      unit: c.lca_unit ?? '',
                      scope1: c.lca_scope1 ?? 0,
                      scope2: c.lca_scope2 ?? 0,
                      scope3: c.lca_scope3 ?? 0,
                      breakdown: c.lca_breakdown ?? [],
                    },
                    timestamp: now,
                    synthetic: true,
                  });
                }
              }
              if (c.tea_total != null) {
                const alreadyHasTea = cCaseId
                  ? s.workspaceHistory.some(h => h.actionType === 'get_tea_results' && h.data?.case_id === cCaseId)
                  : s.workspaceHistory.some(
                      h => h.actionType === 'get_tea_results' && h.label === `TEA — ${pathwayLabel}`,
                    );
                if (!alreadyHasTea) {
                  s.workspaceHistory.push({
                    id: `${now}-synth-tea-${ci}`,
                    actionType: 'get_tea_results',
                    label: `TEA — ${pathwayLabel}`,
                    icon: typeIcon('get_tea_results'),
                    data: {
                      case_id: cCaseId,
                      pathway_id: c.pathway_id,
                      total: c.tea_total,
                      unit: c.tea_unit ?? '',
                      breakdown: c.tea_breakdown ?? [],
                    },
                    timestamp: now,
                    synthetic: true,
                  });
                }
              }
            });
          }
        }
      });
      return;
    }

    // Default: update pendingActions.
    set(s => {
      s._nextTextIsNewBubble = true;
      s.cumulativeStepsDone += 1;
      const runningIdx = s.pendingActions.findIndex(a => a.actionType === actionType && a.status === 'running');
      if (runningIdx !== -1) {
        s.pendingActions[runningIdx] = { actionType, data, status: 'done' };
      } else {
        s.pendingActions.push({ actionType, data, status: 'done' });
      }
    });
  },

  sendMessage: async ({ message }) => {
    if (!message.trim() || get().isStreaming || get().isLoadingConversation) return;

    const currentConversationId = get().conversationId;
    const currentStudyMode = get().studyMode;

    const controller = new AbortController();
    set(s => {
      s.messages.push({ role: 'user', content: message });
      s.isStreaming = true;
      s.abortController = controller;
      s.pendingActions = [];
      s.cumulativeStepsDone = 0;
      s.cumulativeStepsTotal = 0;
    });

    try {
      await streamAnalystResponse({
        message,
        conversationId: currentConversationId,
        studyMode: currentStudyMode,
        signal: controller.signal,

        onProjectId: (projectId, meta = {}) => {
          const { conversationId = null, source = 'supplied', previousProjectName = null } = meta;
          const prevId = get().projectId;
          const prevName = get().projectName;

          if (source === 'replaced') {
            // Backend auto-created a new project because the supplied one
            // was deleted or no longer accessible. Carry the chat thread
            // forward, drop stale workspace state, insert a non-AI
            // notice in the chat so the user knows what happened.
            const displayName = previousProjectName || prevName || null;
            const noticeText = displayName
              ? `Previous project "${displayName}" is no longer available — continuing in a new project. Earlier results have been cleared.`
              : 'Previous project is no longer available — continuing in a new project. Earlier results have been cleared.';

            set(s => {
              s.conversationId = conversationId ?? s.conversationId;
              s.projectId = projectId;
              s.projectName = null;
              // Clear workspace state — case_ids in here point at the
              // deleted project and won't resolve.
              s.workspaceStatus = 'empty';
              s.workspaceHistory = [];
              s.workspaceTabs = [];
              s.workspaceActiveTab = null;
              s.workspaceCaseIdToName = {};
              s.workspaceCaseIdToPathwayId = {};
              s.workspaceAssumptions = {};
              s.workspaceTopologies = {};
              s.workspaceReview = null;
              s.workspaceResultsStale = false;
              s.cumulativeStepsDone = 0;
              s.cumulativeStepsTotal = 0;
              s.messages.push({
                role: 'system',
                kind: 'project_replaced',
                content: noticeText,
                previousProjectName: displayName,
                createdAt: Date.now(),
              });
            });
            analystActions.resolveProjectName(projectId);
            return;
          }

          set(s => {
            s.conversationId = conversationId ?? s.conversationId;
            s.projectId = projectId;
            if (projectId !== prevId) s.projectName = null;
          });
          if (projectId && projectId !== prevId) {
            analystActions.resolveProjectName(projectId);
          }
        },

        onPlan: actions => {
          set(s => {
            s._nextTextIsNewBubble = true;
            s.cumulativeStepsTotal += actions.length;
            for (const actionType of actions) {
              s.pendingActions.push({ actionType, data: null, status: 'pending' });
            }
          });
        },

        onActionStarted: actionType => {
          set(s => {
            s._nextTextIsNewBubble = true;
            s.streamingText = ''; // Discard any intermediate text before this tool call.
            if (s.workspaceHistory.length === 0 && !s.workspaceReview) {
              s.workspaceStatus = 'running';
            }
            const pendingIdx = s.pendingActions.findIndex(a => a.actionType === actionType && a.status === 'pending');
            if (pendingIdx !== -1) {
              s.pendingActions[pendingIdx].status = 'running';
            } else {
              // Agent is retrying a previously-failed tool. Remove the stale
              // error entry so the UI doesn't permanently show a ✗ for a step
              // that ultimately succeeds.
              const errorIdx = s.pendingActions.findIndex(
                a => a.actionType === actionType && a.status === 'done' && a.data?.error,
              );
              if (errorIdx !== -1) s.pendingActions.splice(errorIdx, 1);
              s.pendingActions.push({ actionType, data: null, status: 'running' });
            }
          });
        },

        onThinking: text => {
          set(s => {
            const last = s.messages[s.messages.length - 1];
            if (last?.role === 'thinking') {
              last.content += text;
            } else {
              s.messages.push({ role: 'thinking', content: text });
            }
          });
        },

        onText: text => {
          set(s => {
            const startNew = s._nextTextIsNewBubble;
            s._nextTextIsNewBubble = false;
            s.streamingText += text;
            const last = s.messages[s.messages.length - 1];
            if (!startNew && last?.role === 'assistant') {
              last.content += text;
            } else {
              s.messages.push({
                role: 'assistant',
                content: text,
                intermediate: s.pendingActions.length > 0,
              });
            }
          });
        },

        onDebugStats: data => {
          set(s => {
            s.debugStats = data;
            s.debugStatsHistory.push(data);
            // Attach stats to the most recent assistant message so they
            // persist in the chat history (visible on scroll-back).
            for (let i = s.messages.length - 1; i >= 0; i--) {
              if (s.messages[i].role === 'assistant' && !s.messages[i].hiddenFromUI) {
                s.messages[i].debugStats = data;
                break;
              }
            }
          });
        },

        onAction: analystActions.handleAction,

        onDone: () => {
          // Capture caseIds that have a known pathway but no assumptions yet.
          // The agent skips get_study_assumptions when it goes straight from
          // build → balance → analyze → results without firing propose_analysis,
          // so the Assumptions tab would otherwise stay at "Reviewing
          // assumptions…" forever. Backfill via the same endpoint the
          // node-edit refresh uses.
          const pre = get();
          const missingAssumptionCaseIds = Object.keys(pre.workspaceCaseIdToName ?? {}).filter(
            id => !pre.workspaceAssumptions?.[id],
          );

          set(s => {
            s.isStreaming = false;
            s.abortController = null;
            s.streamingText = '';
            for (let i = s.messages.length - 1; i >= 0; i--) {
              if (s.messages[i].role === 'assistant') {
                s.messages[i].intermediate = false;
                break;
              }
            }
            s.pendingActions = [];
            s.workspaceResultsStale = false;
            s.workspaceStatus = hasWorkspaceContent(s) ? 'results' : 'empty';
          });

          for (const caseId of missingAssumptionCaseIds) {
            fetchCaseAssumptions(caseId)
              .then(data =>
                set(s => {
                  s.workspaceAssumptions[caseId] = data;
                }),
              )
              .catch(() => {});
          }
        },

        onError: errorMessage => {
          set(s => {
            s.isStreaming = false;
            s.abortController = null;
            s.streamingText = '';
            s.messages.push({ role: 'assistant', content: `Something went wrong: ${errorMessage}` });
            if (s.workspaceStatus === 'running') {
              s.workspaceStatus = hasWorkspaceContent(s) ? 'results' : 'empty';
            }
          });
        },
      });
    } catch {
      set(s => {
        s.isStreaming = false;
        s.abortController = null;
        s.messages.push({ role: 'assistant', content: 'Something went wrong. Please try again.' });
      });
    }
  },
};

export default useAnalystStore;
